import React, { useEffect, useId, useState } from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  MapPin,
  RefreshCw,
  Truck,
  X,
} from 'lucide-react';
import { validatePermission } from 'actions';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import ResizableTable from '../ResizableTable';
import IdentityLink from '../IdentityLink';
import driverPhoneColumn from '../driverPhoneColumn';
import { formatDate, formatDocument, formatMoney } from '../contract';
import { upperCase } from '../text';
import useCandidates from './useCandidates';
import {
  CANDIDATES_PER_PAGE,
  SITUATIONS,
  canReadCandidates,
  linkLabel,
  statusLabel,
  platesLabel,
  bodyLabel,
  routeLabel,
  distanceLabel,
  vehiclePositionLabel,
  candidateBlock,
  windowLabel,
  availabilityLabel,
  incompleteLabel,
} from './contract';
import '../styles.css';
import './styles.css';

const blockLabels = {
  ELIGIBLE: 'Fila de candidatos elegíveis',
  REFUSED: 'Recusados nesta carga',
  INCOMPLETE: 'Cadastro incompleto — não pode ser atendido nem recusado (motivo na coluna Ações)',
};

export default function CandidateModal({ context, rowIntegrations = {}, onClose, onChanged }) {
  const titleId = useId();
  const radiusId = useId();
  const statusId = useId();
  // Na volta do atendimento, a consulta recomeça como estava (context.restore).
  const { restore } = context;
  const [radius, setRadius] = useState(restore ? String(restore.radiusKm) : '500');
  const [status, setStatus] = useState(restore?.status || 'ALL');
  const [page, setPage] = useState(restore?.page || 1);
  const [selection, setSelection] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(restore?.candidateId || null);
  const [notice, setNotice] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [rowError, setRowError] = useState(null);
  const noticeKey = JSON.stringify([context.companyId, context.item.loadId, context.item.type]);
  // 5655: a confirmação da recusa fica 2 segundos sobre a lista, que atualiza por baixo.
  useEffect(() => {
    if (!confirmation) return undefined;
    const timer = setTimeout(() => setConfirmation(null), 2000);
    return () => clearTimeout(timer);
  }, [confirmation]);
  const radiusValid =
    radius.trim() !== '' &&
    Number.isFinite(Number(radius)) &&
    Number(radius) >= 0 &&
    Number(radius) <= 500;
  const allowed = canReadCandidates(validatePermission);
  const radiusKm = radiusValid ? Number(radius) : 500;
  const { data, loading, error, errorCode, refresh, queryKey } = useCandidates({
    context,
    enabled: allowed && radiusValid,
    radiusKm,
    status,
    page,
  });
  const rows = data?.data || [];
  const selected =
    selection?.queryKey === queryKey
      ? rows.find((row) => row.candidateId === selection.id) || null
      : null;
  const detailsContext = {
    ...context,
    query: {
      ...context.query,
      candidates: { radiusKm, status, page, perPage: CANDIDATES_PER_PAGE },
    },
  };
  const select = (candidate) => setSelection({ queryKey, id: candidate.candidateId });
  useEffect(() => {
    if (!pendingSelection || loading || !data) return;
    setPendingSelection(null);
    if (!(data.data || []).some((row) => row.candidateId === pendingSelection)) return;
    setSelection({ queryKey, id: pendingSelection });
    requestAnimationFrame(() =>
      document
        .querySelector(`.tc-c-modal [data-row-key="${CSS.escape(pendingSelection)}"]`)
        ?.scrollIntoView({ block: 'center' }),
    );
  }, [pendingSelection, loading, data, queryKey]);
  const extension = (key, candidate) => {
    const Component = rowIntegrations[key];
    return Component ? (
      <Component
        key={queryKey}
        candidate={candidate}
        context={detailsContext}
        onChanged={(message) => {
          if (typeof message === 'string') setNotice({ key: noticeKey, text: message });
          else if (message?.confirmation) {
            setConfirmation({ key: noticeKey, ...message.confirmation });
            setNotice(message.notice ? { key: noticeKey, text: message.notice } : null);
          }
          refresh();
        }}
        onRowError={(message) =>
          setRowError(message ? { queryKey, candidateId: candidate.candidateId, message } : null)
        }
        onAttended={onChanged}
      />
    ) : null;
  };
  const columns = [
    {
      key: 'actions',
      label: 'Ações',
      width: 125,
      minWidth: 100,
      value: (row) => (row.evaluation === 'INCOMPLETE' ? incompleteLabel(row) : ''),
      addon: (row) =>
        extension('candidateActions', row) || (
          <div className="tc-c-row-actions">
            <button
              type="button"
              disabled
              title="Atendimento indisponível no momento"
              aria-label={`Atender ${platesLabel(row)} — indisponível`}
            >
              <Check size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              disabled
              title="Recusa indisponível no momento"
              aria-label={`Recusar ${platesLabel(row)} — indisponível`}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        ),
    },
    {
      key: 'link',
      label: 'Vínculo',
      width: 180,
      minWidth: 80,
      value: linkLabel,
      addon: (row) => extension('candidateLink', row),
    },
    {
      key: 'plates',
      label: 'Placa (cavalo / carreta)',
      width: 160,
      minWidth: 110,
      value: platesLabel,
      render: (row) => (
        <span className="tc-c-plate-stack">
          {extension('candidateRefusalInfo', row)}
          <span className="tc-c-plate-text">
            {[
              { path: `/veiculos/${row.vehicle.id}`, asset: row.vehicle, kind: 'cavalo' },
              ...row.wagons.map((w) => ({
                path: `/implementos/${w.id}`,
                asset: w,
                kind: 'carreta',
              })),
            ].map(({ path, asset, kind }, index) => (
              <React.Fragment key={path}>
                {index > 0 && ' / '}
                <IdentityLink
                  to={path}
                  title={`Abrir cadastro do ${kind} ${asset.plate}`}
                  aria-label={`Abrir cadastro do ${kind} ${asset.plate}`}
                >
                  {asset.plate}
                </IdentityLink>
              </React.Fragment>
            ))}
          </span>
        </span>
      ),
      addon: (row) => extension('candidatePlate', row),
    },
    { key: 'body', label: 'Carroceria | Eixos', width: 140, minWidth: 90, value: bodyLabel },
    {
      key: 'driver',
      label: 'Motorista',
      width: 165,
      minWidth: 100,
      value: (row) => row.driver?.name || 'Não informado',
    },
    driverPhoneColumn(),
    {
      key: 'position',
      label: 'Data/Hora Posição',
      width: 142,
      value: vehiclePositionLabel,
    },
    {
      key: 'distance',
      label: 'KM até Entrega | KM até Coleta',
      width: 145,
      value: distanceLabel,
    },
    {
      key: 'status',
      label: 'Status',
      width: 165,
      value: statusLabel,
      render: (row, value) => (
        <span className="tc-c-status-cell">
          <span
            className={`tc-c-status tc-c-status-${row.status === 'EMPTY' ? 'empty' : 'transit'}`}
          >
            {value}
          </span>
          {/* Número da viagem atual no CooperPlace (o mesmo #id do card do Kanban). */}
          {row.status === 'IN_TRANSIT' && row.currentTravelId && (
            <IdentityLink
              to={`/viagens/${row.currentTravelId}`}
              aria-label={`Abrir viagem atual ${row.currentTravelId} do veículo ${platesLabel(row)}`}
              title={`Abrir viagem ${row.currentTravelId}`}
            >
              #{row.currentTravelId}
            </IdentityLink>
          )}
        </span>
      ),
    },
    { key: 'route', label: 'Rota | CC', width: 190, minWidth: 110, value: routeLabel },
    {
      key: 'arrival',
      label: 'Prev. Entrega',
      width: 140,
      value: (row) => (row.status === 'EMPTY' ? '—' : formatDate(row.estimatedDeliveryAt)),
    },
    {
      key: 'availability',
      label: 'Disponibilidade',
      width: 190,
      value: availabilityLabel,
    },
    {
      key: 'window',
      label: 'Janela',
      width: 175,
      value: windowLabel,
    },
  ];
  const MapView = rowIntegrations.candidateMap;
  const FooterActions = rowIntegrations.candidateFooter;
  const quoted = context.item.amount === null && context.item.sourceAmount != null;
  const amount = formatMoney(
    quoted ? context.item.sourceAmount : context.item.amount,
    quoted ? context.item.sourceCurrency : 'BRL',
  );
  const kind = context.item.type === 'PRE_LOAD' ? 'Pré Carga' : 'Carga';
  function changeRadius(value) {
    setRadius(value);
    setPage(1);
  }
  return (
    <Modal
      show
      handleClose={onClose}
      className="tc-c-modal"
      aria-labelledby={titleId}
      heading={
        <span className="tc-c-heading" id={titleId}>
          <Truck size={23} aria-hidden="true" /> VEÍCULOS DISPONÍVEIS
        </span>
      }
      body={
        <div className="tc-c-body" aria-busy={loading}>
          {confirmation?.key === noticeKey && (
            <div className="tc-c-confirmation" role="status">
              <div>
                <CircleCheck size={48} aria-hidden="true" />
                <strong>{confirmation.title}</strong>
                <span>{confirmation.text}</span>
              </div>
            </div>
          )}
          {/* 5652: os três trechos em maiúsculas, sem negrito, com a mesma cor e o mesmo tamanho. */}
          <p className="tc-c-load">
            <IdentityLink
              to={`/${context.item.type === 'PRE_LOAD' ? 'pre-cargas' : 'cargas'}/${context.item.loadId}`}
              title={`Abrir ${kind} ${context.item.loadId}`}
            >
              {kind} {context.item.loadId}
            </IdentityLink>
            <span aria-hidden="true"> · </span>
            <span>
              Valor a receber: {amount}
              {quoted && ' · Total em reais não informado'}
            </span>
            <span aria-hidden="true"> · </span>
            <span title={upperCase(context.item.client?.name) || ''}>
              {upperCase(context.item.client?.name) || 'Cliente não informado'} |{' '}
              {formatDocument(context.item.client?.cnpj)}
            </span>
          </p>
          <div className="tc-c-controls">
            <div className="tc-c-radius">
              <label htmlFor={radiusId}>Raio:</label>
              <input
                type="range"
                min="0"
                max="500"
                step="1"
                aria-label="Ajustar raio de veículos"
                aria-valuetext={`${radiusValid ? radiusKm : 0} quilômetros`}
                value={radiusValid ? radiusKm : 0}
                disabled={!allowed}
                onChange={(event) => changeRadius(event.target.value)}
              />
              <input
                id={radiusId}
                type="number"
                min="0"
                max="500"
                step="any"
                aria-label="Raio em quilômetros"
                aria-invalid={!radiusValid}
                value={radius}
                disabled={!allowed}
                onChange={(event) => changeRadius(event.target.value)}
              />
              <span>km</span>
            </div>
            <div className="tc-c-situation">
              <label htmlFor={statusId}>Veículos:</label>
              <select
                id={statusId}
                value={status}
                disabled={!allowed}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                }}
              >
                {SITUATIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={!allowed || !radiusValid || loading}
              onClick={refresh}
            >
              <RefreshCw size={14} aria-hidden="true" /> Atualizar veículos
            </Button>
          </div>
          {notice?.key === noticeKey && (
            <p role="status" className="tc-c-message">
              {notice.text}
            </p>
          )}
          {!allowed && (
            <p role="alert" className="tc-c-message">
              Você não tem permissão para consultar os veículos desta carga.
            </p>
          )}
          {!radiusValid && (
            <p role="alert" className="tc-c-message">
              Informe um raio de 0 a 500 km.
            </p>
          )}
          {error && (
            <div role="alert" className="tc-k-error">
              <span>{error}</span>
              <Button size="sm" onClick={refresh}>
                Tentar novamente
              </Button>
              {errorCode === 409 && (
                // A carga pode ter sido atendida ou vencido depois que o quadro foi carregado.
                <Button size="sm" variant="secondary" onClick={() => onChanged?.()}>
                  Atualizar Kanban
                </Button>
              )}
            </div>
          )}
          {data?.operationalUniverse.state === 'PARTIAL' && (
            <p role="status" className="tc-c-message">
              Consulta incompleta: {data.operationalUniverse.pendingCount} veículos aguardam
              confirmação. A lista mostra apenas os confirmados. Atualize a consulta para liberar
              “Atender com outro”.
            </p>
          )}
          <div className="tc-c-workspace">
            <div className="tc-c-table-area">
              {loading && (
                <p role="status" className="tc-c-message">
                  Consultando veículos e distâncias…
                </p>
              )}
              {data && !rows.length && (
                <p role="status" className="tc-c-message">
                  {data.totalCount
                    ? 'Nenhum veículo nesta página. Retorne à primeira página.'
                    : 'Nenhum veículo encontrado para esta consulta.'}
                </p>
              )}
              <ResizableTable
                columns={columns}
                rows={rows}
                label="Veículos disponíveis para a carga"
                rowKey={(row) => row.candidateId}
                rowHeading={(row, index) =>
                  index === 0 || candidateBlock(row) !== candidateBlock(rows[index - 1])
                    ? blockLabels[candidateBlock(row)]
                    : null
                }
                rowProps={(row) => ({
                  className: `tc-c-row tc-c-${candidateBlock(row).toLowerCase()}${selected?.candidateId === row.candidateId ? ' tc-c-selected' : ''}`,
                  onClick: (event) => {
                    if (!event.target.closest('button,a,input,select')) select(row);
                  },
                })}
                renderRowDetails={(row) =>
                  rowError?.queryKey === queryKey && rowError.candidateId === row.candidateId ? (
                    <div className="tc-c-row-error" role="alert">
                      <span>{rowError.message}</span>
                      <Button
                        size="xs"
                        variant="secondary"
                        onClick={() => {
                          setRowError(null);
                          refresh();
                        }}
                      >
                        Atualizar fila
                      </Button>
                    </div>
                  ) : null
                }
              />
            </div>
            <aside className="tc-c-map" aria-label="Localização do veículo">
              <h3>
                <MapPin size={16} aria-hidden="true" /> Localização do veículo
              </h3>
              {MapView ? (
                <MapView
                  candidate={selected}
                  context={detailsContext}
                  overview={
                    data
                      ? { pickup: data.pickup, points: data.mapPoints, radiusKm: data.radiusKm }
                      : null
                  }
                />
              ) : (
                <div className="tc-c-map-state" role="status">
                  <MapPin size={38} aria-hidden="true" />
                  <strong>{selected ? platesLabel(selected) : 'Nenhum veículo selecionado'}</strong>
                  <span>
                    {selected
                      ? 'Mapa indisponível no momento.'
                      : 'Selecione uma linha para consultar a localização.'}
                  </span>
                  {selected && !selected.position && (
                    <small>Posição do veículo não informada.</small>
                  )}
                </div>
              )}
            </aside>
          </div>
        </div>
      }
      footer={
        <div className="tc-c-footer">
          <div role="status" className="tc-c-count">
            {data ? (
              <>
                <strong>{data.totalCount} registros</strong>
                <span>
                  {data.eligibleCount} elegíveis
                  {data.incompleteCount > 0 && ` · ${data.incompleteCount} com cadastro incompleto`}
                </span>
                <small>
                  {data.data.length} nesta página · Consulta: {formatDate(data.evaluatedAt)}
                </small>
              </>
            ) : (
              <span>Quantidade não consultada</span>
            )}
          </div>
          <div className="tc-c-pagination">
            <Button
              size="xs"
              variant="secondary"
              aria-label="Página anterior de veículos"
              disabled={!data || page <= 1}
              onClick={() => setPage((value) => value - 1)}
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </Button>
            <span>
              Página {page} de {data?.pagination.totalPages || 1}
            </span>
            <Button
              size="xs"
              variant="secondary"
              aria-label="Próxima página de veículos"
              disabled={!data?.pagination.hasMore}
              onClick={() => setPage((value) => value + 1)}
            >
              <ChevronRight size={16} aria-hidden="true" />
            </Button>
            {data && page > 1 && !rows.length && (
              <Button size="xs" onClick={() => setPage(1)}>
                Primeira página
              </Button>
            )}
          </div>
          <div className="tc-c-footer-actions">
            {FooterActions && data ? (
              <FooterActions
                key={queryKey}
                context={detailsContext}
                data={data}
                onChanged={refresh}
                onAttended={onChanged}
              />
            ) : (
              <Button
                size="sm"
                variant="danger"
                outlined
                disabled
                title="Atendimento indisponível no momento"
              >
                Atender com outro
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      }
    />
  );
}
